import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Intent } from '@blueprintjs/core';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LinkButton } from 'components/buttons';
import { DataDisplayRecord } from 'components/data-display';
import { DateToLocal } from 'components/dates';
import { GridLayout } from 'components/grid';
import { ShowHide } from 'components/layout';
import { Breadcrumbs } from 'components/navigation';
import { AlertConfirm, AlertUnsavedData, NotificationInline, NotificationToaster } from 'components/notifications';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { Wizard, WizardSteps } from 'components/wizard';
import { useDirtyData } from 'hooks/useDirtyData';
import { buildAdditionalTriggersGating, toApiDate } from 'pages/employee-reviews/complete/employeeReviewCompleteHelpers';
import { AdditionalTriggersStep, OutcomeStep } from 'pages/employee-reviews/complete/steps';
import { EditSignOffStep } from 'pages/employee-reviews/completed/edit/steps';
import {
    getNextReviewDateBlockedMessage,
    getTopicIdsByKeys,
    getTriggerValidationRules,
    mapTriggersFromApi,
    toSignatureValue,
    toTriggerErrors
} from 'pages/employee-reviews/shared';
import { PageRestricted } from 'pages/errors/page-restricted';
import { EmployeeReviewsService } from 'services';
import { buildLoadedTopicStates, getEditTopics } from './editTopics';
import {
    buildEditReviewRequest,
    buildSaveGating,
    getLoadedOutcome,
    hasAnyChanges,
    getSaveConfirmationMessage,
    hasSignOffResettingChanges,
    hasSignOffStep
} from './employeeReviewEditHelpers';

const performAction = "EmployeeReview:Perform";
const viewAction = "EmployeeReview:View";
const listingRoute = "/employee-reviews/completed-reviews";
const badRequestCode = 400;
const noAccessCode = 403;
const defaultArray = [];
const defaultGating = { canProceed: true, cantProceedTooltip: null };
const cleanDirtyState = { changed: false };
const completedDateFormat = "DD/MM/YYYY";

// The edit wizard for a completed review: a step per topic on the review, then Outcome, the
// driver's additional triggers where the review offers them, and Sign-off only when the edit can
// collect a signature. It works only on what the completed review recorded, so it reads the review
// and the review options and nothing else.
export function EmployeeReviewEdit({ breadcrumbs = defaultArray }) {

    const { reviewId } = useParams();
    const navigate = useNavigate();
    const canPerformReviews = UserHasAccess(performAction);
    const canViewReviews = UserHasAccess(viewAction);
    const canEditReview = canPerformReviews && canViewReviews;
    const reviewRoute = `${listingRoute}/${reviewId}`;

    const [loading, setLoading] = useState(true);
    const [invalidReview, setInvalidReview] = useState(false);
    const [review, setReview] = useState(null);
    const [reviewOptions, setReviewOptions] = useState({});

    const [topicStates, setTopicStates] = useState({});
    const [outcomeLevelNumber, setOutcomeLevelNumber] = useState(null);
    const [outcomeSummary, setOutcomeSummary] = useState("");
    const [nextReviewDate, setNextReviewDate] = useState(null);
    const [employeeTriggers, setEmployeeTriggers] = useState(defaultArray);
    const [loadedEmployeeTriggers, setLoadedEmployeeTriggers] = useState(defaultArray);
    // The signatures are personal data, so they are held only here until they are sent.
    const [employeeSignature, setEmployeeSignature] = useState(null);
    const [employeeSignedAt, setEmployeeSignedAt] = useState(null);
    const [managerSignature, setManagerSignature] = useState(null);
    const [managerSignedAt, setManagerSignedAt] = useState(null);

    const [wizardStep, setWizardStep] = useState(0);
    const [saving, setSaving] = useState(false);
    const [confirmingSave, setConfirmingSave] = useState(false);

    const topics = useMemo(() => getEditTopics(review), [review]);

    const answers = {
        review,
        topics,
        topicStates,
        outcomeLevelNumber,
        outcomeSummary,
        nextReviewDate,
        employeeTriggers,
        loadedEmployeeTriggers,
        reviewOptions,
        employeeSignature,
        managerSignature
    };

    const resetsSignOff = review != null && hasSignOffResettingChanges(answers);
    const hasDraftWork = topics.some((topic) => topic.isDirty(topicStates[topic.key]));
    const changed = review != null && (hasAnyChanges(answers) || hasDraftWork);
    const dirtyState = useMemo(() => ({ changed }), [changed]);
    const isDirty = useDirtyData(cleanDirtyState, dirtyState);

    // Puts every step back to what the completed review recorded.
    function applyLoadedReview(loadedReview, options) {
        const loadedOutcome = getLoadedOutcome(loadedReview);
        const loadedTriggers = mapTriggersFromApi(loadedReview?.triggerSettings?.employee, options);

        setTopicStates(buildLoadedTopicStates(getEditTopics(loadedReview)));
        setOutcomeLevelNumber(loadedOutcome.outcomeLevelNumber);
        setOutcomeSummary(loadedOutcome.outcomeSummary);
        setNextReviewDate(toApiDate(loadedReview?.nextReviewDate));
        setLoadedEmployeeTriggers(loadedTriggers);
        setEmployeeTriggers(loadedTriggers);
        clearSignatures();
    }

    function clearSignatures() {
        setEmployeeSignature(null);
        setEmployeeSignedAt(null);
        setManagerSignature(null);
        setManagerSignedAt(null);
    }

    // A signature is given for the content as it stood when the Sign-off step was reached. Leaving
    // the step unmounts its pads, and anything changed on another step would no longer be what was
    // signed, so the signatures are dropped and must be given again on returning.
    function onWizardStepChange(step) {
        setWizardStep(step);

        if (step !== wizardSteps.length - 1) {
            clearSignatures();
        }
    }

    useEffect(() => {
        // A read for a review the route has moved off must not land on the review now on screen.
        let active = true;

        if (!canEditReview) {
            return () => {
                active = false;
            };
        }

        setLoading(true);
        setInvalidReview(false);
        setReview(null);
        setWizardStep(0);

        function errorHandler(error) {
            if (!active) {
                return;
            }

            const status = error?.response?.status ?? error?.status;

            if (status === badRequestCode || status === noAccessCode) {
                setInvalidReview(true);
            } else {
                NotificationToaster.show(Intent.DANGER, "Failed to get the review. Please refresh the page.");
            }
        }

        Promise.all([
            EmployeeReviewsService.getCompletedReview(reviewId),
            EmployeeReviewsService.getOptions()
        ]).then(function ([reviewResponse, optionsResponse]) {
            if (!active) {
                return;
            }

            setReviewOptions(optionsResponse);
            applyLoadedReview(reviewResponse, optionsResponse);
            setReview(reviewResponse);
        }, errorHandler).catch(errorHandler).finally(function () {
            if (active) {
                setLoading(false);
            }
        });

        return () => {
            active = false;
        };
    }, [reviewId, canEditReview]);

    function updateTopic(topicKey, changes) {
        setTopicStates(function (previous) {
            const topicState = previous[topicKey];
            const resolvedChanges = typeof changes === "function" ? changes(topicState) : changes;

            return { ...previous, [topicKey]: { ...topicState, ...resolvedChanges } };
        });
    }

    function onManagerSignatureChange(value) {
        const signature = toSignatureValue(value);
        setManagerSignature(signature);
        setManagerSignedAt(signature == null ? null : new Date());
    }

    function onEmployeeSignatureChange(value) {
        const signature = toSignatureValue(value);
        setEmployeeSignature(signature);
        setEmployeeSignedAt(signature == null ? null : new Date());
    }

    function saveChanges() {
        const request = buildEditReviewRequest(answers);

        setConfirmingSave(false);
        setSaving(true);

        EmployeeReviewsService.updateCompletedReview(reviewId, request).then(function () {
            NotificationToaster.show(Intent.SUCCESS, "The review has been updated.");

            // Everything entered is now saved, so the page is put back to the review it loaded -
            // otherwise the unsaved-changes alert would block the redirect. The redirect waits for
            // the end of the render cycle, once the alert has seen the reset.
            applyLoadedReview(review, reviewOptions);
            setTimeout(() => {
                navigate(reviewRoute);
            }, 100);
        }, function (error) {
            if (error?.status === badRequestCode) {
                NotificationToaster.show(Intent.WARNING, error.data);
            } else {
                NotificationToaster.show(Intent.DANGER, "Failed to update the review. Please try again.");
            }
        }).finally(function () {
            setSaving(false);
        });
    }

    function onFinish() {
        if (getSaveConfirmationMessage(answers) != null) {
            setConfirmingSave(true);
            return;
        }

        saveChanges();
    }

    const hasTriggerSettings = review?.triggerSettings != null;

    const reviewTypeTriggers = useMemo(() => {
        return mapTriggersFromApi(review?.triggerSettings?.reviewType, reviewOptions);
    }, [review, reviewOptions]);

    const triggerTopicIds = useMemo(() => {
        return getTopicIdsByKeys((review?.topics ?? defaultArray).map((topic) => topic.topicKey), reviewOptions);
    }, [review, reviewOptions]);

    const triggerValidationRules = useMemo(() => {
        if (!hasTriggerSettings) {
            return defaultArray;
        }

        return getTriggerValidationRules(employeeTriggers, reviewOptions, { coveringTriggers: reviewTypeTriggers });
    }, [hasTriggerSettings, employeeTriggers, reviewOptions, reviewTypeTriggers]);

    function renderTopicStep(topic) {
        const TopicStep = topic.Step;

        return (
            <TopicStep
                key={topic.key}
                state={topicStates[topic.key]}
                topicEntry={topic.topicEntry}
                loading={loading}
                onChange={(changes) => updateTopic(topic.key, changes)}
            />
        );
    }

    const nextReviewDateEditable = review?.nextReviewDateEditable === true;

    // On a review type without a schedule the date is the driver's next review itself. On a scheduled
    // one it moves the next review, and clearing it falls back to the scheduled date.
    let nextReviewDateHelperText = `Set a date to move ${review?.employee?.name}'s next review to it. Leave blank to return to the scheduled review date.`;

    if (review?.scheduleActive === false) {
        nextReviewDateHelperText = `Set the date ${review?.employee?.name}'s next review is due. Leave blank for no next review.`;
    }

    let nextReviewDateBlockedMessage = null;

    if (review != null && !nextReviewDateEditable) {
        nextReviewDateBlockedMessage = getNextReviewDateBlockedMessage(review.nextReviewDateBlockedReason, review.employee?.name);
    }

    // The Wizard takes its step titles and its children separately, and both are built from this
    // one list so they cannot disagree.
    const editSteps = [
        ...topics.map((topic) => ({
            stepTitle: topic.stepTitle,
            gating: topic.getGating(topicStates[topic.key]),
            content: renderTopicStep(topic)
        })),
        {
            stepTitle: "Outcome",
            gating: defaultGating,
            content: (
                // A next review date that may not be edited is shown as the recorded date in a
                // disabled field, which says why on hover.
                <OutcomeStep
                    key="OUTCOME"
                    pendingReview={{
                        outcomeLevels: review?.outcomeLevels,
                        allowCustomNextReviewDate: nextReviewDateEditable,
                        scheduleActive: review?.scheduleActive,
                        employee: review?.employee
                    }}
                    reviewOptions={reviewOptions}
                    outcomeLevelNumber={outcomeLevelNumber}
                    outcomeSummary={outcomeSummary}
                    customNextReviewDate={nextReviewDate}
                    nextReviewDateHeadingText="Next review date"
                    nextReviewDateHelperText={nextReviewDateHelperText}
                    earliestNextReviewDate={review?.nextReviewDate}
                    nextReviewDateDisabledReason={nextReviewDateBlockedMessage}
                    loading={loading}
                    saving={saving}
                    onOutcomeLevelChange={setOutcomeLevelNumber}
                    onOutcomeSummaryChange={(event) => setOutcomeSummary(event.target.value)}
                    onCustomNextReviewDateChange={setNextReviewDate}
                />
            )
        },
        ...(hasTriggerSettings ? [{
            stepTitle: "Additional triggers",
            gating: buildAdditionalTriggersGating(triggerValidationRules),
            content: (
                <AdditionalTriggersStep
                    key="ADDITIONALTRIGGERS"
                    reviewTypeTriggers={reviewTypeTriggers}
                    employeeTriggers={employeeTriggers}
                    topicIds={triggerTopicIds}
                    reviewOptions={reviewOptions}
                    errors={toTriggerErrors(triggerValidationRules)}
                    loading={loading}
                    saving={saving}
                    onEmployeeTriggersChange={setEmployeeTriggers}
                />
            )
        }] : []),
        ...(hasSignOffStep(review) ? [{
            stepTitle: "Sign-off",
            gating: defaultGating,
            content: (
                <EditSignOffStep
                    key="SIGNOFF"
                    method={review?.signOff?.method}
                    resetsSignOff={resetsSignOff}
                    requireManagerSignature={review?.signOff?.requireManagerSignature === true}
                    employeeSignature={employeeSignature}
                    employeeSignedAt={employeeSignedAt}
                    managerSignature={managerSignature}
                    managerSignedAt={managerSignedAt}
                    loading={loading}
                    onEmployeeSignatureChange={onEmployeeSignatureChange}
                    onManagerSignatureChange={onManagerSignatureChange}
                />
            )
        }] : [])
    ];

    // SAVE CHANGES sits on the last step, so once that step's own rule is met it is held by the save's.
    const wizardSteps = editSteps.map((step, index) => {
        if (index !== editSteps.length - 1 || !step.gating.canProceed) {
            return step;
        }

        return { ...step, gating: buildSaveGating(answers) };
    });

    const activeStepGating = wizardSteps[wizardStep]?.gating ?? defaultGating;
    const canProceed = !loading && !saving && activeStepGating.canProceed;
    const notEditable = review != null && review.canEdit !== true;
    const employeeId = review?.employee?.id;
    const employeeName = review?.employee?.name;

    const pageBreadcrumbs = [
        ...breadcrumbs,
        { location: reviewRoute, title: "Completed Review" },
        { title: "Edit Review" }
    ];

    function renderEditPage() {
        return (
            <ShowHide
                evaluator={invalidReview}
                show={(
                    <div className="row">
                        <Breadcrumbs items={pageBreadcrumbs} />
                        <NotificationInline
                            allowClose={false}
                            show
                            text="This review does not exist, or you do not have permission to view it"
                            intent="danger"
                        />
                        <div>
                            <LinkButton intent="primary" text="Back to Completed Reviews" id="listing-completed-reviews" href={listingRoute} />
                        </div>
                    </div>
                )}
                hide={(
                    <Fragment>
                        <div className="row">
                            <Breadcrumbs items={pageBreadcrumbs} />
                            <h1 className={classNames({ "bp6-skeleton": loading })}>Edit Review</h1>
                            <GridLayout numberOfColumns={4} className="spacer-bottom-medium">
                                <DataDisplayRecord
                                    loading={loading}
                                    header="Employee"
                                    data={(
                                        <ShowHide
                                            evaluator={!loading && employeeId != null}
                                            show={(
                                                <Link to={`/employee/${employeeId}/reviews`} target="_blank" rel="noopener noreferrer">{employeeName}</Link>
                                            )}
                                            hide={employeeName}
                                        />
                                    )}
                                />
                                <DataDisplayRecord loading={loading} header="Review Type" data={review?.reviewType?.name} />
                                <DataDisplayRecord loading={loading} header="Completed" data={<DateToLocal format={completedDateFormat}>{review?.completedDate}</DateToLocal>} />
                            </GridLayout>
                        </div>
                        <div className="row">
                            <ShowHide
                                evaluator={loading}
                                show={(
                                    // The steps are the review's own topics, so they cannot be built
                                    // until it has arrived. Until then the step strip stands in for them.
                                    <div className="wizard">
                                        <WizardSteps
                                            currentStep={0}
                                            totalSteps={0}
                                            loading
                                            minimal
                                            showStepTitles
                                            onStepClick={() => null}
                                        />
                                    </div>
                                )}
                                hide={(
                                    <ShowHide
                                        evaluator={notEditable}
                                        show={(
                                            <Fragment>
                                                <NotificationInline
                                                    allowClose={false}
                                                    show
                                                    text="This review cannot be edited."
                                                    intent="warning"
                                                />
                                                <div>
                                                    <LinkButton intent="primary" text="Back to Review" id="completed-review" href={reviewRoute} />
                                                </div>
                                            </Fragment>
                                        )}
                                        hide={(
                                            <ShowHide
                                                evaluator={review != null}
                                                show={(
                                                    <Wizard
                                                        key={reviewId}
                                                        onStepChange={onWizardStepChange}
                                                        onFinish={onFinish}
                                                        canProceed={canProceed}
                                                        cantProceedTooltip={activeStepGating.cantProceedTooltip}
                                                        additionalStepInformation={wizardSteps.map((step) => ({ stepTitle: step.stepTitle }))}
                                                        showStepTitles
                                                        // The wizard counts its own steps a commit after mounting, so saving is
                                                        // offered against the step set here rather than against that count.
                                                        canFinish={wizardStep === wizardSteps.length - 1}
                                                        disabled={saving}
                                                        loading={loading}
                                                        alignButtonsRight
                                                        finishButtonText="SAVE CHANGES"
                                                    >
                                                        {wizardSteps.map((step) => step.content)}
                                                    </Wizard>
                                                )}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </div>
                        <AlertConfirm
                            isOpen={confirmingSave}
                            title="Save changes"
                            confirmButtonText="Save changes"
                            confirmButtonIntent="primary"
                            saving={saving}
                            onConfirm={saveChanges}
                            onCancel={() => setConfirmingSave(false)}
                        >
                            <p>{getSaveConfirmationMessage(answers)}</p>
                        </AlertConfirm>
                        <AlertUnsavedData isDirty={isDirty} />
                    </Fragment>
                )}
            />
        );
    }

    // Editing needs the perform-reviews action, and the completed review it edits is read with the
    // view-reviews one, so the page needs both.
    return (
        <UserAccess perform={viewAction}
            yes={() => (
                <UserAccess perform={performAction}
                    yes={renderEditPage}
                    no={() => (
                        <PageRestricted />
                    )}
                />
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

EmployeeReviewEdit.propTypes = {
    breadcrumbs: PropTypes.array
};
